import React, { useState, useEffect, useRef, useCallback } from 'react';
import Markdown from 'react-markdown';
import { apiClient } from '../../utils/api';
import ErrorMessage from '../common/ErrorMessage';
import '../../styles/inbox.css';

const POLL_INTERVAL = 5000;

const AttachmentBadge = ({ attachment }) => (
    <div className="conversation-attachment">
        <span className="conversation-attachment-icon">📎</span>
        <span className="conversation-attachment-name">{attachment.filename}</span>
    </div>
);

const ConversationThread = ({ actionId, onBack }) => {
    const [data, setData] = useState(null);
    const [reply, setReply] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [error, setError] = useState(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    const loadConversation = useCallback(async () => {
        try {
            const result = await apiClient.getActionConversation(actionId);
            setData(prev => {
                const newCount = result.conversation?.length || 0;
                const oldCount = prev?.conversation?.length || 0;
                if (newCount !== oldCount || result.action?.status !== prev?.action?.status) {
                    return result;
                }
                return prev;
            });
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [actionId]);

    useEffect(() => {
        loadConversation();
    }, [loadConversation]);

    // Poll for updates when action is not in a terminal state
    useEffect(() => {
        if (!data) return;
        const status = data.action?.status;
        if (status === 'completed' || status === 'failed') return;

        const interval = setInterval(loadConversation, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [data, loadConversation]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [data]);

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setError(null);
        try {
            const result = await apiClient.uploadActionImage(actionId, file);
            setPendingAttachments(prev => [...prev, result.attachment]);
        } catch (err) {
            setError(err.message || 'Failed to upload image');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();
        if ((!reply.trim() && pendingAttachments.length === 0) || sending) return;

        setSending(true);
        try {
            const message = reply.trim() || (pendingAttachments.length > 0
                ? `Attached ${pendingAttachments.length} photo(s)` : '');
            const attachments = pendingAttachments.length > 0 ? pendingAttachments : null;
            const result = await apiClient.replyToActionRequest(actionId, message, attachments);
            setData(result);
            setReply('');
            setPendingAttachments([]);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div className="inbox-loading">Loading conversation...</div>;
    if (error && !data) return <ErrorMessage title="Error" message={error} />;
    if (!data) return null;

    const { action, conversation } = data;
    const isWaiting = action.status === 'waiting_for_user';
    const isEscalated = (() => {
        try { return JSON.parse(action.result || '{}').type === 'escalated'; } catch { return false; }
    })();

    const formatPrompt = (prompt) => {
        try {
            const p = JSON.parse(prompt);
            return `Vendor: ${p.vendor_of_interferer || 'unknown'} | SSID: ${p.SSID_of_interferer || 'unknown'}`;
        } catch {
            return prompt;
        }
    };

    return (
        <div className="conversation-container">
            <div className="conversation-header">
                <button className="conversation-back-btn" onClick={onBack}>&larr; Back</button>
                <div className="conversation-header-info">
                    <span className="conversation-type">Wi-Fi Interference</span>
                    <span className={`conversation-status conversation-status-${action.status}`}>
                        {isEscalated ? 'Contact Support' : action.status.replace(/_/g, ' ')}
                    </span>
                </div>
                <div className="conversation-mac">{formatPrompt(action.prompt)}</div>
            </div>

            <div className="conversation-messages">
                {conversation.map((msg, idx) => (
                    <div key={idx} className={`conversation-msg conversation-msg-${msg.direction}`}>
                        <div className="conversation-msg-label">
                            {msg.direction === 'agent_to_user' ? 'Agent' : 'You'}
                        </div>
                        <div className="conversation-msg-bubble">
                            <Markdown>{msg.message}</Markdown>
                        </div>
                        {msg.attachments && msg.attachments.length > 0 && (
                            <div className="conversation-attachments">
                                {msg.attachments.map((att, i) => (
                                    <AttachmentBadge key={i} attachment={att} />
                                ))}
                            </div>
                        )}
                        <div className="conversation-msg-time">
                            {new Date(msg.created_at).toLocaleString()}
                        </div>
                    </div>
                ))}

                {(sending || action.status === 'processing') && (
                    <div className="conversation-msg conversation-msg-agent_to_user">
                        <div className="conversation-msg-label">Agent</div>
                        <div className="conversation-msg-bubble conversation-processing">
                            <div className="conversation-typing">
                                <span></span><span></span><span></span>
                            </div>
                            <span className="conversation-processing-text">
                                {sending ? 'Analysing your response...' : 'Investigating the issue...'}
                            </span>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {error && <div style={{ padding: '0 20px' }}><ErrorMessage title="Error" message={error} /></div>}

            {isWaiting && (
                <div className="conversation-reply-area">
                    {pendingAttachments.length > 0 && (
                        <div className="conversation-pending-attachments">
                            {pendingAttachments.map((att, i) => (
                                <AttachmentBadge key={i} attachment={att} />
                            ))}
                        </div>
                    )}
                    <form className="conversation-reply-form" onSubmit={handleSend}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/jpeg,image/png,image/webp,image/heic"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                        <button
                            type="button"
                            className="conversation-attach-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={sending || uploading}
                            title="Attach photo"
                        >
                            {uploading ? '...' : '📷'}
                        </button>
                        <input
                            type="text"
                            className="conversation-reply-input"
                            placeholder="Type your reply..."
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            disabled={sending}
                        />
                        <button
                            type="submit"
                            className="conversation-reply-btn"
                            disabled={sending || (!reply.trim() && pendingAttachments.length === 0)}
                        >
                            {sending ? '...' : 'Send'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

const AgentInbox = ({ onView }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedActionId, setSelectedActionId] = useState(null);

    const loadRequests = useCallback(async () => {
        try {
            const result = await apiClient.getActionRequests();
            setRequests(result.requests || []);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRequests();
        if (onView) onView();
        const interval = setInterval(loadRequests, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [loadRequests, onView]);

    if (selectedActionId) {
        return (
            <ConversationThread
                actionId={selectedActionId}
                onBack={() => {
                    setSelectedActionId(null);
                    loadRequests();
                }}
            />
        );
    }

    return (
        <div className="inbox-dashboard">
            <header className="inbox-header">
                <div className="inbox-header-icon">📥</div>
                <div className="inbox-header-text">
                    <h2>Agent Inbox</h2>
                    <p>Device identification requests from the system</p>
                </div>
            </header>

            <div className="inbox-list-container">
                {loading && <div className="inbox-loading">Loading requests...</div>}
                {error && <ErrorMessage title="Error" message={error} />}

                {!loading && requests.length === 0 && (
                    <div className="inbox-empty">No pending requests. You're all caught up!</div>
                )}

                {requests.map((req) => (
                    <div
                        key={req.id}
                        className={`inbox-item ${req.action_status === 'waiting_for_user' ? 'inbox-item-urgent' : ''}`}
                        onClick={() => setSelectedActionId(req.action_id)}
                    >
                        <div className="inbox-item-header">
                            <span className="inbox-item-type">{req.action_type}</span>
                            <span className={`inbox-item-status inbox-item-status-${req.action_status}`}>
                                {req.action_status === 'waiting_for_user' ? '⏳ Needs Reply' :
                                 req.action_status === 'processing' ? '⚙️ Processing' :
                                 req.action_status === 'completed' ? '✅ Resolved' : req.action_status}
                            </span>
                        </div>
                        <div className="inbox-item-message"><Markdown>{req.message}</Markdown></div>
                        <div className="inbox-item-meta">
                            <span className="inbox-item-mac">{(() => {
                                try {
                                    const p = JSON.parse(req.action_prompt);
                                    return `${p.vendor_of_interferer || 'Unknown'} | ${p.SSID_of_interferer || 'Unknown'}`;
                                } catch { return req.action_prompt; }
                            })()}</span>
                            <span className="inbox-item-time">
                                {new Date(req.created_at).toLocaleString()}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AgentInbox;
